import React, { useEffect, useMemo, useState } from 'react';
import {
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { fetchAdmin, postAdmin, safeJsonParse, toErrorMessage } from '../../admin/api';
import { AdminRouteLayout, RequestState, StatCard, useAsyncState } from './AdminRoutePageShared';

function AdminConfirmationDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  loading = false,
  error = '',
  success = '',
  onClose,
  onConfirm,
}) {
  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>

          {error ? (
            <Box
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 1,
                bgcolor: 'error.50',
                color: 'error.main',
                border: '1px solid',
                borderColor: 'error.200',
              }}
            >
              <Typography variant="body2">{error}</Typography>
            </Box>
          ) : null}

          {success ? (
            <Box
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 1,
                bgcolor: 'success.50',
                color: 'success.main',
                border: '1px solid',
                borderColor: 'success.200',
              }}
            >
              <Typography variant="body2">{success}</Typography>
            </Box>
          ) : null}

          {loading ? (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="body2">Processing request...</Typography>
            </Stack>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={loading} variant="text">
          {cancelLabel}
        </Button>
        <Button onClick={onConfirm} disabled={loading} variant="outlined">
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function formatTimestamp(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

function highlightStatus(status) {
  if (status === 'dispatched') return 'success.main';
  if (status === 'failed') return 'error.main';
  return 'warning.main';
}

export default function QueueOutboxPage() {
  const { enqueueSnackbar } = useSnackbar();
  const overview = useAsyncState(null);
  const items = useAsyncState([]);
  const [pageMeta, setPageMeta] = useState({
    current_page: 1,
    total_pages: 1,
    total_size: 0,
    page_size: 50,
  });
  const [filters, setFilters] = useState({
    page: 1,
    page_size: 50,
    queue_name: '',
    job_type: '',
    status: '',
    resource_id: '',
  });
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');


  useEffect(() => {
    let active = true;

    const loadItems = async () => {
      const isRefresh = !initialLoading && Boolean(items.state.data?.length);
      if (isRefresh) setRefreshing(true);
      else items.setLoading();

      try {
        const response = await fetchAdmin('/ops/queues/outbox', filters);

        const data = response.items?.data || [];
        const meta = response.items || {};

        if (!active) return;
        items.setData(data);
        setPageMeta({
          current_page: meta.current_page ?? 1,
          total_pages: meta.total_pages ?? 1,
          total_size: meta.total_size ?? 0,
          page_size: meta.page_size ?? filters.page_size,
        });
      } catch (error) {
        if (!active) return;
        items.setError(toErrorMessage(error));
      } finally {
        if (!active) return;
        setInitialLoading(false);
        setRefreshing(false);
      }
    };

    loadItems();
    return () => {
      active = false;
    };
  }, [filters.page, filters.page_size, filters.queue_name, filters.job_type, filters.status, filters.resource_id]);

  const rows = useMemo(() => {
    return (items.state.data || []).map((row) => ({
      id: row.id,
      jobType: row.job_type || '—',
      queue: row.queue_name || '—',
      payload: safeJsonParse(row.payload_json, {}),
      resourceType: row.resource_type || '—',
      resourceId: row.resource_id || '—',
      status: row.status || 'unknown',
      attempts: row.attempts ?? 0,
      lastError: row.last_error || '—',
      availableAt: formatTimestamp(row.available_at),
      dispatchedAt: formatTimestamp(row.dispatched_at),
      createdAt: formatTimestamp(row.created_at),
      raw: row,
    }));
  }, [items.state.data]);

  const closeActionDialog = () => {
    if (actionLoading) return;
    setPendingAction(null);
    setActionError('');
    setActionSuccess('');
  };

  const runAction = async () => {
    if (!pendingAction) return;
    setActionLoading(true);
    setActionError('');
    setActionSuccess('');

    try {
      await postAdmin(pendingAction.path);
      setActionSuccess(pendingAction.success);
      enqueueSnackbar(pendingAction.success, { variant: 'success' });

      const response = await fetchAdmin('/ops/queues/outbox', filters);
      const data = response.items?.data || [];
      const meta = response.items || {};

      items.setData(data);
      setPageMeta({
        current_page: meta.current_page ?? 1,
        total_pages: meta.total_pages ?? 1,
        total_size: meta.total_size ?? 0,
        page_size: meta.page_size ?? filters.page_size,
      });
    } catch (error) {
      const message = toErrorMessage(error);
      setActionError(message);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <AdminRouteLayout title="Outbox" breadcrumbTitle="Queue & Jobs">
      <Stack spacing={3}>

        <RequestState loading={items.state.loading} error={items.state.error}>
          <Card variant="outlined">
            <CardContent sx={{ p: 0, position: 'relative' }}>
              {initialLoading ? (
                <Box sx={{ minHeight: 420, display: 'grid', placeItems: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box sx={{ position: 'relative' }}>
                  <TableContainer sx={{ maxHeight: 'calc(100vh - 340px)' }}>
                    <Table stickyHeader size="medium" aria-label="outbox jobs table">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>ID</TableCell>
                          <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Queue / Job</TableCell>
                          <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Payload</TableCell>
                          <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Attempts</TableCell>
                          <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Dispatched</TableCell>
                          <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.map((row) => (
                          <TableRow key={row.id} hover sx={{ '& td': { py: 1.5 } }}>
                            <TableCell>{row.id}</TableCell>
                            <TableCell>
                              <Box>
                                <Typography variant="body2" fontWeight={600}>
                                  {row.queue}
                                </Typography>
                                <Typography variant="caption">{row.jobType}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: 13,
                                maxWidth: 320,
                                wordBreak: 'break-word',
                              }}
                            >
                              {JSON.stringify(row.payload)}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: highlightStatus(row.status),
                                  }}
                                />
                                <Typography variant="body2" fontWeight={600}>
                                  {row.status}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>{row.attempts}</TableCell>
                            <TableCell>{row.dispatchedAt}</TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={1}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() =>
                                    setPendingAction({
                                      path: `/ops/queues/outbox/${row.id}/retry`,
                                      success: 'Outbox retry accepted',
                                    })
                                  }
                                >
                                  Retry
                                </Button>
                                <Button
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  onClick={() =>
                                    setPendingAction({
                                      path: `/ops/queues/outbox/${row.id}/cancel`,
                                      success: 'Outbox job cancelled',
                                    })
                                  }
                                >
                                  Cancel
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}

                        {!rows.length && !items.state.loading ? (
                          <TableRow>
                            <TableCell colSpan={7} sx={{ py: 8, textAlign: 'center' }}>
                              <Typography variant="subtitle1" fontWeight={600}>
                                No outbox jobs
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                The outbox endpoint returned no records for the current filters.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : null}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {refreshing ? (
                    <Backdrop
                      open
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 1,
                        bgcolor: 'rgba(255,255,255,0.35)',
                        color: 'text.primary',
                      }}
                    >
                      <CircularProgress />
                    </Backdrop>
                  ) : null}

                  <TablePagination
                    component="div"
                    count={pageMeta.total_size}
                    page={Math.max(pageMeta.current_page - 1, 0)}
                    onPageChange={(_, nextPage) => setFilters((prev) => ({ ...prev, page: nextPage + 1 }))}
                    rowsPerPage={filters.page_size}
                    onRowsPerPageChange={(event) =>
                      setFilters((prev) => ({
                        ...prev,
                        page: 1,
                        page_size: parseInt(event.target.value, 10),
                      }))
                    }
                    rowsPerPageOptions={[10, 25, 50]}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </RequestState>
      </Stack>

      <AdminConfirmationDialog
        open={Boolean(pendingAction)}
        title="Confirm outbox action"
        description="This action will be applied directly to the selected outbox record."
        confirmLabel="Run action"
        loading={actionLoading}
        error={actionError}
        success={actionSuccess}
        onClose={closeActionDialog}
        onConfirm={runAction}
      />
    </AdminRouteLayout>
  );
}